import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Form, Button } from "react-bootstrap";
import { Navigate, useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { startAuthentication } from '@simplewebauthn/browser';
import { sanitizarTexto } from '../utils/sanitizarTexto';
// import Swal from 'sweetalert2';

const LOGIN_URL =  `${process.env.REACT_APP_API_URL}/auth/login`;
const PASSKEY_LOGIN_OPTIONS_URL = `${process.env.REACT_APP_API_URL}/auth/webauthn/login/options`;
const PASSKEY_LOGIN_VERIFY_URL = `${process.env.REACT_APP_API_URL}/auth/webauthn/login/verify`;
//const LOGIN_URL =  `${process.env.REACT_APP_API_URL}`;

const IconoOjo = ({ visible }) => {
    if (visible) {
        // ojo tachado
        return (
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path
                    fill="currentColor"
                    d="M2.1 3.51L3.51 2.1 21.9 20.49 20.49 21.9l-2.16-2.16A11.7 11.7 0 0 1 12 21C6 21 2.73 16.89 1 12c.7-2.02 1.77-3.82 3.13-5.31L2.1 3.51ZM12 7c6 0 9.27 4.11 11 9a15.63 15.63 0 0 1-2.93 4.57l-1.43-1.43A13.47 13.47 0 0 0 21 16c-1.73-4.89-5-9-9-9-1.03 0-2 .22-2.9.6L7.74 6.24C9.05 5.48 10.49 5 12 5c1.77 0 3.4.45 4.86 1.19l-1.45 1.45A6.97 6.97 0 0 0 12 7Zm0 2a4.98 4.98 0 0 1 4.12 2.17l-1.44 1.44A3 3 0 0 0 12 11a3 3 0 0 0-1.61.47L8.94 10.02A4.95 4.95 0 0 1 12 9Zm0 6a3 3 0 0 0 1.53-.42l-3.95-3.95A3 3 0 0 0 12 15Z"
                />
            </svg>
        );
    }

    // ojo normal
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
                fill="currentColor"
                d="M12 5c6 0 9.27 4.11 11 9-1.73 4.89-5 9-11 9S2.73 18.89 1 14c1.73-4.89 5-9 11-9Zm0 2c-4 0-7.27 4.11-9 7 1.73 2.89 5 7 9 7s7.27-4.11 9-7c-1.73-2.89-5-7-9-7Zm0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"
            />
        </svg>
    );
};

export function LoginForm() {

    const { setAuth } = useAuth();
    const userRef = useRef();
    const errRef = useRef();
    const [user, setUser] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [blocked, setBlocked] = useState(false);
    const [blockedMessage, setBlockedMessage] = useState("");
    const navigate = useNavigate();
    const [mostrarPassword, setMostrarPassword] = useState(false);

    const [passkeyLoading, setPasskeyLoading] = useState(false);
    const [passkeyError, setPasskeyError] = useState('');
    const [passkeysDisponible, setPasskeysDisponible] = useState(false);

    const estiloContenedorPassword = {
        position: "relative",
        display: "block"
    };

    const estiloOjo = {
        position: "absolute",
        right: "0px",
        top: "0px",
        height: "100%",
        width: "44px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: "#666666",
        zIndex: 2
    };

    // Detectar si es mobile
    const esMobile = () => {
        const ua = (navigator.userAgent || '').toLowerCase();
        if (ua.includes('android') || ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return true;

        // fallback por touch (evita algunos casos raros)
        const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
        return !!isTouch && (window.innerWidth <= 1024);
    };

    const getTextoBotonPasskey = () => {
        const mobile = esMobile();
        const platform = (navigator.userAgentData?.platform || navigator.platform || '').toLowerCase();

        if (mobile) {
            return 'Ingresar con huella / FaceID';
        }

        // Desktop
        if (platform.includes('win')) return 'Ingresar con (PIN/huella)';
        if (platform.includes('mac')) return 'Ingresar con Touch ID';
        return 'Ingresar con llave de acceso';
    };

    // Detectar si el navegador/dispositivo soporta autenticación con passkeys (huella/pin)
    useEffect(() => {
        const check = async () => {
            try {
                const ok = await window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable?.();
                setPasskeysDisponible(!!ok);
            } catch (e) {
                setPasskeysDisponible(false);
            }
        };
        check();
    }, []);

    const handleLoginWithPasskey = async () => {
        try {
            setPasskeyLoading(true);
            setPasskeyError("");
            setError("");
            setBlocked(false);
            setBlockedMessage("");

            // 1) Pedir options
            const opt = await axios.get(PASSKEY_LOGIN_OPTIONS_URL, {
                headers: { 'Content-Type': 'application/json' }
            });

            const desafio_id = opt?.data?.desafio_id;
            const options = opt?.data?.options;

            if (!desafio_id || !options) {
                setPasskeyError("No se pudo iniciar el ingreso con llave de acceso.");
                return;
            }

            // 2) Abrir prompt del navegador
            const authResp = await startAuthentication(options);

            // 3) Verificar en el back y obtener token
            const res = await axios.post(
                PASSKEY_LOGIN_VERIFY_URL,
                JSON.stringify({ desafio_id, response: authResp }),
                { headers: { 'Content-Type': 'application/json' } }
            );

            const token = res?.data?.token;
            const roles = res?.data?.user?.roles;
            const username = res?.data?.user?.fullname;
            const passwordExpirada = !!res?.data?.password?.expirado;

            const userNameFromApi = res?.data?.user?.name || '';

            setAuth({ user: userNameFromApi, roles, token, username });
            window.localStorage.setItem('auth', JSON.stringify({ user: userNameFromApi, roles, token, username }));

            if (passwordExpirada) {
                navigate("/cambiarPasswordVencida", { state: { email: userNameFromApi } });
                return;
            }

            navigate("/");
        } catch (err) {
            const msg = err?.response?.data?.message || 'No se pudo ingresar con llave de acceso.';
            const lower = msg.toLowerCase();

            let esBloqueo = false;
            let mensajeBloqueo = "";

            if (
                lower.includes('bloqueado') ||
                lower.includes('contraseña está vencida') ||
                lower.includes('contraseña esta vencida') ||
                lower.includes('vencimiento de la contraseña')
            ) {
                esBloqueo = true;
                if (lower.includes('vencimiento de la contraseña')) {
                    mensajeBloqueo = msg;
                } else if (lower.includes('bloqueado')) {
                    mensajeBloqueo = 'Usuario bloqueado por intentos fallidos. Por favor recuperá tu contraseña usando el botón "Olvidé mi contraseña".';
                } else {
                    mensajeBloqueo = msg;
                }
            }

            window.localStorage.removeItem('auth');

            if (esBloqueo) {
                setBlocked(true);
                setBlockedMessage(mensajeBloqueo);
                setError(msg);
            } else {
                setPasskeyError(msg);
            }
        } finally {
            setPasskeyLoading(false);
        }
    };

    const handleSubmit = async (e)=>{
        //e.preventDefault();

        if (!validarFormulario()) {
            setError('Revisar datos');
            return;
        }

        try{

            const res = await axios.post(LOGIN_URL,
                JSON.stringify({name:user, password}),
                {
                    headers:{'Content-Type':'application/json'},
                    //withCredentials:true
                });

            const token = res?.data?.token;
            const roles = res?.data?.user?.roles;
            const username = res?.data?.user?.fullname;
            const passwordExpirada = !!res?.data?.password?.expirado;

            setAuth({user, roles, token, username})

            window.localStorage.setItem('auth', JSON.stringify({user, roles, token, username}))

            //document.cookie = `token=${token};max-age=${60*3};path=/;samesite=strict`;
            setUser("");
            setPassword("");
            setBlocked(false);
            setError("");

            if (passwordExpirada) {
                navigate("/cambiarPasswordVencida", { state: { email: user } });
                return;
            }
            navigate("/")
        }
        catch(err){
            const msg = err?.response?.data?.message || 'Usuario o contraseña incorrecto';

            setError(msg);
            window.localStorage.removeItem('auth');
            const lower = msg.toLowerCase();

            let esBloqueo = false;
            let mensajeBloqueo = "";

            if (
                lower.includes('bloqueado') ||
                lower.includes('contraseña está vencida') ||
                lower.includes('contraseña esta vencida') ||
                lower.includes('vencimiento de la contraseña')
            ) {
                esBloqueo = true;
                if (lower.includes('vencimiento de la contraseña')) {
                    mensajeBloqueo = msg;
                } else if (lower.includes('bloqueado')) {
                    mensajeBloqueo = 'Usuario bloqueado por intentos fallidos. Por favor recuperá tu contraseña usando el botón "Olvidé mi contraseña".';
                } else {
                    mensajeBloqueo = msg;
                }
            }

            setBlocked(esBloqueo);
            setBlockedMessage(mensajeBloqueo);
        }

    }

    const validarFormulario = ()=> {
        if (!user || !password) {
            return false;
        }
        return true;
    }

    useEffect(()=>{
        if (userRef.current) {
            userRef.current.focus()
        }
    }, [])

    useEffect(()=>{
        setError("");
        setPasskeyError("");
    }, [user, password])


    const getErrorStyle = () => {
        if (!error && !blocked) return {};
        if (blocked) {
            return {
                marginTop: "20px",
                textAlign: "center",
                color: "#ff4d4f",
                fontWeight: "bold"
            };
        }
        return {
            marginTop: "20px",
            textAlign: "center",
            color: "#d9a300",
            fontWeight: "bold"
        };
    };

    return (
        <div className="body-login">
            <div className="row header">
                <div className="col-md-3 d-none d-md-block">
                    <p></p>
                </div>
                <div className="col-md-6 col-sm-12 p-0 logo-container">
                    <img className="logo-cortado" src="/Logo-Ethical.png"/>
                </div>
                <div className="col-md-3 d-none d-md-block">
                    <p></p>
                </div>
            </div>

            <div className="row body">
                <div className="col-md-3 d-none d-md-block">
                    <p></p>
                </div>
                <div className="col-md-6 col-sm-12 body-content">
                    <p className="title">Login</p>

                    <div className="form-group">
                        <p className="label"><span><img src="message.png"/></span> <span className="label-text">Usuario</span></p>
                        <p><input className="form-text" type="text"  name ="name" ref={userRef} autoComplete='off' onChange={(e)=>{setUser(sanitizarTexto(e.target.value))}} value={user} required/></p>
                    </div>

                    <div className="form-group">
                        <p className="label"><span><img src="lock.png"/></span> <span className="label-text">Contraseña</span></p>

                        <p style={estiloContenedorPassword}>
                            <input
                                className="form-text"
                                type={mostrarPassword ? "text" : "password"}
                                name ="password"
                                onChange={(e)=>{setPassword(e.target.value)}}
                                value={password}
                                required
                                style={{ paddingRight: "52px" }}
                            />

                            <span
                                role="button"
                                tabIndex={0}
                                style={estiloOjo}
                                onClick={()=>{ setMostrarPassword(!mostrarPassword) }}
                                onKeyDown={(e)=>{
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setMostrarPassword(!mostrarPassword);
                                    }
                                }}
                                aria-label={mostrarPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                                title={mostrarPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                            >
                                <IconoOjo visible={mostrarPassword} />
                            </span>
                        </p>
                    </div>

                    <div className="form-group-actions">


                        {(error || blocked) && (
                                <p style={getErrorStyle()}>
                                    {blocked
                                        ? (blockedMessage || error)
                                        : error}
                                </p>
                            )}

                        {passkeyError && !blocked && (
                            <p style={{
                                marginTop: "20px",
                                textAlign: "center",
                                color: "#ff4d4f",
                                fontWeight: "bold"
                            }}>
                                {passkeyError}
                            </p>
                        )}

                        {!blocked && (
                            <button className="btn-primary" onClick={()=>{handleSubmit()}}>
                                <p>Ingresar</p>
                            </button>
                        )}

                        {!blocked && passkeysDisponible && (
                            <button
                                className="btn-primary"
                                style={{
                                    backgroundColor: '#0A9E95',
                                    borderColor: '#0A9E95',
                                    color: '#ffffff',
                                    marginTop: '10px'
                                }}
                                disabled={passkeyLoading}
                                onClick={handleLoginWithPasskey}
                            >
                                <p>{passkeyLoading ? "Verificando..." : getTextoBotonPasskey()}</p>
                            </button>
                        )}

                        <button
                            className="btn-primary"
                            style={{
                                backgroundColor: '#666666',   // gris más oscuro
                                borderColor: '#666666',
                                color: '#ffffff',
                                marginTop: blocked ? '0' : '10px'
                            }}
                            onClick={()=>{ navigate('/recuperar-contrasena') }}
                        >
                            <p>Olvidé mi contraseña</p>
                        </button>
                    </div>



                    <p style={{fontHeight:"8px!important", textAlign:"center", marginTop:"30px", color:"#bcbcbc"}}>v{process.env.REACT_APP_VERSION} {(process.env.REACT_APP_ENVIRONMENT && process.env.REACT_APP_ENVIRONMENT=='QA')?' - QA':''}</p>
                </div>
                <div className="col-md-3 d-none d-md-block">
                    <p></p>
                </div>
            </div>

            {/* <div className="limiter">
                <div className="container-login100">
                    <div className="wrap-login100">
                        {mostrarAlerta &&
                        <div className="alert alert-danger" role="alert">
                            Usuario o contraseña inválidos
                        </div>
                        }

                        <Form onSubmit={handleSubmit}>
                            <p className="text-center mb-3"><img src="./lac-light.png"></img></p>
                            <div className="col-12">
                                <Form.Label className="pt-3"> Nombre </Form.Label>
                                <Form.Control type="text" name ="name" ref={userRef} autoComplete='off' onChange={(e)=>{setUser(sanitizarTexto(e.target.value))}} value={user} required/>
                            </div>
                            <div className="col-12">
                                <Form.Label className="pt-3"> Password </Form.Label>
                                <Form.Control type="password" name ="password"  onChange={(e)=>{setPassword(e.target.value)}} value={password} required/>
                            </div>
                            <div className="col-12 mt-5 text-center">
                                <Button type="submit" variant="primary">
                                    Ingresar
                                </Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div> */}
        </div>
    )
}